import axios from "axios";
import { hideChildModal, hideModal } from "./modal";

/*****************/
/* INITIAL STATE */
/*****************/
const initialState = {
  loading: false,
  error: "",
  data: [],
  query: { ids: [], total: null },
};

/*********/
/* TYPES */
/*********/
const FETCH_SELECTED_CONTAINERS = "FETCH_SELECTED_CONTAINERS";
const FETCH_SELECTED_CONTAINERS_SUCCESS = "FETCH_SELECTED_CONTAINERS_SUCCESS";
const FETCH_SELECTED_CONTAINERS_ERROR = "FETCH_SELECTED_CONTAINERS_ERROR";
const FETCH_NEXT_SELECTED_CONTAINERS = "FETCH_NEXT_SELECTED_CONTAINERS";
const FETCH_NEXT_SELECTED_CONTAINERS_SUCCESS = "FETCH_NEXT_SELECTED_CONTAINERS_SUCCESS";

const UPDATE_SELECTED_CONTAINER = "UPDATE_SELECTED_CONTAINER";
const UPDATE_SELECTED_CONTAINER_SUCCESS = "UPDATE_SELECTED_CONTAINER_SUCCESS";
const UPDATE_SELECTED_CONTAINER_ERROR = "UPDATE_SELECTED_CONTAINER_ERROR";

const FETCH_CONTAINER = "FETCH_CONTAINER";
const FETCH_CONTAINER_SUCCESS = "FETCH_CONTAINER_SUCCESS";
const FETCH_CONTAINER_ERROR = "FETCH_CONTAINER_ERROR";

const CREATE_SELECTED_CONTAINER = "CREATE_SELECTED_CONTAINER";
const CREATE_SELECTED_CONTAINER_SUCCESS = "CREATE_SELECTED_CONTAINER_SUCCESS";
const CREATE_SELECTED_CONTAINER_ERROR = "CREATE_SELECTED_CONTAINER_ERROR";

const DELETE_SELECTED_CONTAINER = "DELETE_SELECTED_CONTAINER";
const DELETE_SELECTED_CONTAINER_SUCCESS = "DELETE_SELECTED_CONTAINER_SUCCESS";
const DELETE_SELECTED_CONTAINER_ERROR = "DELETE_SELECTED_CONTAINER_ERROR";

/*******************/
/* HELPERS */
/*******************/

const MAX_IDS_PER_REQUEST = 500;

const getIdParams = (ids) =>
  ids && ids.length
    ? ids.map((id) => `&id[]=${encodeURIComponent(id)}`).join("")
    : "";

const chunkArray = (arr, size) => {
  const result = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
};

/*******************/
/* ACTION CREATORS */
/*******************/
export const getSelectedContainers =
  (ids = [], options = {}) =>
  async (dispatch, getState) => {
    const validIds = ids.filter((id) => {
      if (typeof id === "string" && id.trim() !== "") return true;
      if (typeof id === "number" && Number.isInteger(id)) return true;

      return false;
    });

    if (validIds.length === 0) {
      dispatch({
        type: FETCH_SELECTED_CONTAINERS_SUCCESS,
        payload: { selectedContainers: [], query: { ids: [], total: 0 } },
      });
      return;
    }

    const {
      selectedContainers: { query, data },
    } = getState();

    const { force = false } = options;
    const isSameQuery = JSON.stringify(query.ids) === JSON.stringify(validIds);
    const isDataComplete =
      query.total !== null && data.length === query.total;

    if (!force && isSameQuery && isDataComplete) {
      return;
    }

    dispatch({ type: FETCH_SELECTED_CONTAINERS });

    try {
      const idChunks = chunkArray(validIds, MAX_IDS_PER_REQUEST);
      let allContainers = [];

      for (const chunk of idChunks) {
        const firstRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/containers?page=1${getIdParams(
            chunk
          )}`,
          {
            headers: {
              "Content-Type": "application/ld+json; charset=utf-8",
              Accept: "application/ld+json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        const firstMembers = firstRes.data["hydra:member"] || [];
        allContainers = allContainers.concat(firstMembers);

        let next = firstRes.data?.["hydra:view"]?.["hydra:next"] ?? null;

        while (next) {
          const nextRes = await axios.get(next, {
            headers: {
              "Content-Type": "application/ld+json; charset=utf-8",
              Accept: "application/ld+json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          });

          const nextMembers = nextRes.data["hydra:member"] || [];
          allContainers = allContainers.concat(nextMembers);

          next = nextRes.data?.["hydra:view"]?.["hydra:next"] ?? null;
        }
      }

      const mapById = new Map();
      for (const c of allContainers) {
        if (c && c["@id"]) {
          mapById.set(c["@id"], c);
        }
      }
      const finalContainers = Array.from(mapById.values());
      const total = finalContainers.length;

      dispatch({
        type: FETCH_SELECTED_CONTAINERS_SUCCESS,
        payload: {
          selectedContainers: finalContainers,
          query: { ids: validIds, total },
        },
      });
    } catch (err) {
      dispatch({
        type: FETCH_SELECTED_CONTAINERS_ERROR,
        payload: err.message,
      });
    }
  };

export const getNextSelectedContainers = (nextUrl) => async (dispatch) => {
  dispatch({ type: FETCH_NEXT_SELECTED_CONTAINERS });

  try {
    const response = await axios.get(nextUrl, {
      headers: {
        "Content-Type": "application/ld+json; charset=utf-8",
        Accept: "application/ld+json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });

    dispatch({
      type: FETCH_NEXT_SELECTED_CONTAINERS_SUCCESS,
      payload: response.data["hydra:member"] || [],
    });

    const next = response.data["hydra:view"]?.["hydra:next"];
    if (next) dispatch(getNextSelectedContainers(next));
  } catch (err) {
    dispatch({ type: FETCH_SELECTED_CONTAINERS_ERROR, payload: err.message });
  }
};

export const updateSelectedContainer =
  (container, allowHideModal = true) =>
  async (dispatch, getState) => {
    dispatch({ type: UPDATE_SELECTED_CONTAINER });

    const { modal } = getState();

    try {
      const id = container["@id"].split("/").pop();
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/containers/${id}`,
        container,
        {
          headers: {
            "Content-Type": "application/ld+json",
            Accept: "application/ld+json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      dispatch({
        type: UPDATE_SELECTED_CONTAINER_SUCCESS,
        payload: response.data,
      });

      if (allowHideModal) {
        if (modal.childModal) {
          dispatch(hideChildModal());
        } else {
          dispatch(hideModal());
        }
      }
    } catch (err) {
      dispatch({ type: UPDATE_SELECTED_CONTAINER_ERROR, payload: err.message });
    }
  };

export const getContainerById = (containerIri) => async (dispatch) => {
  dispatch({ type: FETCH_CONTAINER });
  try {
    const id = containerIri.split("/").pop();
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/containers/${id}`,
      {
        headers: {
          "Content-Type": "application/ld+json; charset=utf-8",
          Accept: "application/ld+json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );
    dispatch({
      type: FETCH_CONTAINER_SUCCESS,
      payload: response.data,
    });
  } catch (err) {
    dispatch({
      type: FETCH_CONTAINER_ERROR,
      payload: err.message,
    });
  }
};

export const createSelectedContainer =
  (container, allowHideModal = true) =>
  async (dispatch) => {
    dispatch({ type: CREATE_SELECTED_CONTAINER });

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/containers`,
        container,
        {
          headers: {
            "Content-Type": "application/ld+json",
            Accept: "application/ld+json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      dispatch({
        type: CREATE_SELECTED_CONTAINER_SUCCESS,
        payload: response.data,
      });

      if (allowHideModal) {
        dispatch(hideChildModal());
        dispatch(hideModal());
      }

      return response.data;
    } catch (err) {
      dispatch({
        type: CREATE_SELECTED_CONTAINER_ERROR,
        payload: err.message,
      });
    }
  };

export const deleteSelectedContainer = (containerIri) => async (dispatch) => {
  dispatch({ type: DELETE_SELECTED_CONTAINER });
  try {
    const id = containerIri.split("/").pop();

    await axios.delete(
      `${process.env.REACT_APP_API_URL}/containers/${id}`,
      {
        headers: {
          "Content-Type": "application/ld+json",
          Accept: "application/ld+json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    dispatch({
      type: DELETE_SELECTED_CONTAINER_SUCCESS,
      payload: containerIri,
    });
  } catch (err) {
    dispatch({
      type: DELETE_SELECTED_CONTAINER_ERROR,
      payload: err.message,
    });
  }
};

export const loadSelectedContainers = () => ({
  type: FETCH_SELECTED_CONTAINERS,
});
export const loadNextSelectedContainers = () => ({
  type: FETCH_NEXT_SELECTED_CONTAINERS,
});
export const setError = (msg) => ({
  type: FETCH_SELECTED_CONTAINERS_ERROR,
  payload: msg,
});

/***********/
/* REDUCER */
/***********/
const selectedContainerReducer = (state = initialState, { type, payload }) => {
  switch (type) {
    case FETCH_SELECTED_CONTAINERS:
      return { ...state, loading: true, error: "" };
    case FETCH_SELECTED_CONTAINERS_SUCCESS:
      return {
        ...state,
        loading: false,
        error: "",
        data: payload.selectedContainers,
        query: payload.query,
      };
    case FETCH_SELECTED_CONTAINERS_ERROR:
      return { ...state, loading: false, error: payload };
    case FETCH_NEXT_SELECTED_CONTAINERS:
      return { ...state, error: "" };
    case FETCH_NEXT_SELECTED_CONTAINERS_SUCCESS: {
      const existingIds = new Set(state.data.map((c) => c["@id"]));
      const uniqueNewItems = payload.filter((c) => !existingIds.has(c["@id"]));

      return {
        ...state,
        loading: false,
        data: [...state.data, ...uniqueNewItems],
      };
    }
    case UPDATE_SELECTED_CONTAINER:
      return { ...state, loading: true, error: "" };
    case UPDATE_SELECTED_CONTAINER_SUCCESS:
      return {
        ...state,
        loading: false,
        data: state.data.map((c) =>
          c["@id"] === payload["@id"] ? payload : c
        ),
      };
    case UPDATE_SELECTED_CONTAINER_ERROR:
      return { ...state, loading: false, error: payload };
    case FETCH_CONTAINER:
      return { ...state, loading: true, error: "" };
    case FETCH_CONTAINER_SUCCESS: {
      const existingIndex = state.data.findIndex(
        (c) => c["@id"] === payload["@id"]
      );

      let newData;
      if (existingIndex !== -1) {
        newData = state.data.map((c) =>
          c["@id"] === payload["@id"] ? payload : c
        );
      } else {
        newData = [...state.data, payload];
      }

      return {
        ...state,
        loading: false,
        error: "",
        data: newData,
      };
    }
    case CREATE_SELECTED_CONTAINER:
      return { ...state, loading: true, error: "" };
    case CREATE_SELECTED_CONTAINER_SUCCESS:
      return { ...state, loading: false, data: [...state.data, payload] };
    case CREATE_SELECTED_CONTAINER_ERROR:
      return { ...state, loading: false, error: payload };
    case DELETE_SELECTED_CONTAINER:
      return { ...state, loading: true, error: "" };
    case DELETE_SELECTED_CONTAINER_SUCCESS:
      return {
        ...state,
        loading: false,
        data: state.data.filter((c) => c["@id"] !== payload),
      };
    case DELETE_SELECTED_CONTAINER_ERROR:
      return { ...state, loading: false, error: payload };
    default:
      return state;
  }
};

export default selectedContainerReducer;